// SPDX-License-Identifier: AGPL-3.0-or-later

import {SpoilerOverlay} from '@app/features/app/components/shared/SpoilerOverlay';
import spoilerStyles from '@app/features/app/components/shared/SpoilerOverlay.module.css';
import {EmbedGif, EmbedGifv} from '@app/features/channel/components/embeds/media/EmbedGifv';
import {EmbedImage} from '@app/features/channel/components/embeds/media/EmbedImage';
import EmbedVideo from '@app/features/channel/components/embeds/media/EmbedVideo';
import type {Message} from '@app/features/messaging/models/Message';
import {getAttachmentMediaDimensions} from '@app/features/messaging/utils/MediaDimensionConfig';
import {buildMediaProxyURL} from '@app/features/messaging/utils/MediaProxyUtils';
import {determineMediaType} from '@app/features/messaging/utils/MediaViewerItemUtils';
import {useSpoilerState} from '@app/features/messaging/utils/SpoilerUtils';
import styles from '@app/features/theme/styles/AttachmentSingleMedia.module.css';
import {createCalculator} from '@app/features/ui/utils/DimensionUtils';
import {MessageAttachmentFlags} from '@app/vendor/constants/ChannelConstants';
import type {MessageAttachment} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {observer} from 'mobx-react-lite';
import type {CSSProperties, FC, ReactElement} from 'react';
export interface AttachmentSingleMediaProps {
	attachment: MessageAttachment;
	message?: Message;
	mediaAttachments: ReadonlyArray<MessageAttachment>;
	isPreview?: boolean;
	snapshotIndex?: number;
	onDelete?: (bypassConfirm?: boolean) => void;
}
interface AttachmentMediaConstraintStyle extends CSSProperties {
	'--attachment-media-max-height': string;
	'--attachment-media-max-width': string;
}
export const AttachmentSingleMedia: FC<AttachmentSingleMediaProps> = observer(
	({attachment, message, mediaAttachments, isPreview, snapshotIndex, onDelete}) => {
		const mediaType = determineMediaType(attachment);
		const isGifv = mediaType === 'gifv';
		const isVideo = mediaType === 'video';
		const isAnimatedGif = mediaType === 'gif';
		const isSpoiler = (attachment.flags & MessageAttachmentFlags.IS_SPOILER) !== 0;
		const nsfw = attachment.nsfw || (attachment.flags & MessageAttachmentFlags.CONTAINS_EXPLICIT_MEDIA) !== 0;
		const {hidden: spoilerHidden, reveal: revealSpoiler} = useSpoilerState(isSpoiler, message?.channelId);
		const wrapSpoiler = (node: ReactElement) =>
			isSpoiler ? (
				<SpoilerOverlay hidden={spoilerHidden} onReveal={revealSpoiler} className={spoilerStyles.media}>
					{node}
				</SpoilerOverlay>
			) : (
				node
			);
		const naturalWidth = attachment.width!;
		const naturalHeight = attachment.height!;
		const attachmentDimensions = getAttachmentMediaDimensions(message);
		const standaloneMediaCalculator = createCalculator({
			maxWidth: attachmentDimensions.maxWidth,
			maxHeight: attachmentDimensions.maxHeight,
			responsive: true,
		});
		const mediaConstraintStyle: AttachmentMediaConstraintStyle = {
			'--attachment-media-max-height': `${attachmentDimensions.maxHeight}px`,
			'--attachment-media-max-width': `${attachmentDimensions.maxWidth}px`,
		};
		const {dimensions} = standaloneMediaCalculator.calculate(
			{
				width: naturalWidth,
				height: naturalHeight,
			},
			{forceScale: true},
		);
		const safeProxy = attachment.proxy_url ?? attachment.url ?? '';
		const safeUrl = attachment.url ?? '';
		const commonProps = {
			channelId: message?.channelId,
			messageId: message?.id,
			attachmentId: attachment.id,
			message,
			contentHash: attachment.content_hash,
			placeholder: attachment.placeholder,
			nsfw,
			onDelete,
			snapshotIndex,
		};
		if (isGifv) {
			return wrapSpoiler(
				<div className={styles.relativeWrapper} style={mediaConstraintStyle}>
					<div className={styles.singleMediaContainer}>
						<EmbedGifv
							{...commonProps}
							embedURL={safeUrl}
							videoProxyURL={safeProxy}
							videoURL={safeUrl}
							naturalWidth={naturalWidth}
							naturalHeight={naturalHeight}
							isPreview={isPreview}
							alt={attachment.description ?? undefined}
						/>
					</div>
				</div>,
			);
		}
		if (isVideo) {
			return wrapSpoiler(
				<div className={styles.relativeWrapper} style={mediaConstraintStyle}>
					<div className={styles.singleMediaContainer}>
						<EmbedVideo
							{...commonProps}
							src={safeProxy}
							width={dimensions.width}
							height={dimensions.height}
							title={attachment.title || attachment.filename}
							alt={attachment.description ?? undefined}
							mediaAttachments={mediaAttachments}
							isPreview={isPreview}
						/>
					</div>
				</div>,
			);
		}
		if (isAnimatedGif) {
			const animatedProxyURL = buildMediaProxyURL(attachment.proxy_url ?? attachment.url ?? '', {
				format: 'webp',
				animated: true,
			});
			return wrapSpoiler(
				<div className={styles.relativeWrapper} style={mediaConstraintStyle}>
					<div className={styles.singleMediaContainer}>
						<EmbedGif
							{...commonProps}
							embedURL={safeUrl}
							proxyURL={animatedProxyURL}
							naturalWidth={naturalWidth}
							naturalHeight={naturalHeight}
							isPreview={isPreview}
							alt={attachment.description ?? undefined}
						/>
					</div>
				</div>,
			);
		}
		const targetWidth = Math.round(dimensions.width * 2);
		const targetHeight = Math.round(dimensions.height * 2);
		const optimizedSrc = buildMediaProxyURL(attachment.proxy_url ?? attachment.url ?? '', {
			format: 'webp',
			width: targetWidth,
			height: targetHeight,
		});
		return wrapSpoiler(
			<div className={styles.relativeWrapper} style={mediaConstraintStyle}>
				<div className={styles.singleMediaContainer}>
					<EmbedImage
						{...commonProps}
						src={optimizedSrc}
						originalSrc={safeUrl}
						naturalWidth={naturalWidth}
						naturalHeight={naturalHeight}
						width={dimensions.width}
						height={dimensions.height}
						constrain={true}
						mediaAttachments={mediaAttachments}
						isPreview={isPreview}
						animated={isAnimatedGif}
						alt={attachment.description ?? undefined}
					/>
				</div>
			</div>,
		);
	},
);
